/**
 * AfterSalesWorkspace.jsx — AFTER-SALES-WORKSPACE-001
 * ====================================================
 *
 * Dense, grid-first operational workspace for the
 * `provider_after_sales_rep` role (with read-only supervisory
 * access for `provider_admin`).
 *
 * Visual authority: COORDINATOR-GRID-UX-001. Reuses the same
 * journey-ux primitives that power /clinic/operations and
 * /doctor/workspace. NO cards. NO dashboard tiles.
 *
 * Six tabbed views:
 *   1. Recoveries   - Recovery Queue grid (per case)
 *   2. Follow-Ups   - upcoming / missed / overdue / completed rows
 *   3. Evidence     - DOCUMENT / CONSENT / PATIENT_ACTION / TASK
 *   4. Escalations  - escalation queue (sourced from revisions)
 *   5. History      - chronological revisions
 *   6. Completion   - approaching closure / closed / re-open
 *
 * Three drawer-footer clinical actions on unsatisfied rows for
 * after-sales reps only:
 *   - Mark followed up   → POST /action {action: "followed_up"}
 *   - Request update     → POST /action {action: "request_update"}
 *   - Escalate           → POST /action {action: "escalate"}
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LifeBuoy, RefreshCw, ExternalLink, CheckCircle2, RotateCcw, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../../utils/api';
import {
  DataGrid,
  DetailDrawer,
  DrawerField,
  FilterChips,
  Pill,
} from '../../components/journey-ux';

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */
const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  } catch { return '—'; }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return '—'; }
};
const numPill = (n, tone = 'rose') => n > 0 ? <Pill tone={tone}>{n}</Pill> : <span className="text-slate-300">—</span>;

const FOLLOWUP_TONE = {
  upcoming: 'sky', missed: 'amber', overdue: 'rose',
  completed: 'emerald', escalated: 'rose', waiting_patient: 'amber',
  needs_follow_up: 'amber', active: 'sky',
};

/* -------------------------------------------------------------------- */
/*  Views                                                               */
/* -------------------------------------------------------------------- */
const VIEWS = [
  { id: 'recoveries',  label: 'Recoveries',  path: 'recoveries' },
  { id: 'followups',   label: 'Follow-Ups',  path: 'followups' },
  { id: 'evidence',    label: 'Evidence',    path: 'evidence' },
  { id: 'escalations', label: 'Escalations', path: 'escalations' },
  { id: 'history',     label: 'History',     path: 'history' },
  { id: 'completion',  label: 'Completion',  path: 'completion' },
];

const BUCKETS = [
  { id: 'all',             label: 'All' },
  { id: 'mine',            label: 'Mine' },
  { id: 'needs_follow_up', label: 'Needs follow-up' },
  { id: 'overdue',         label: 'Overdue follow-ups' },
  { id: 'waiting_patient', label: 'Waiting patient' },
  { id: 'escalated',       label: 'Escalated' },
  { id: 'completed',       label: 'Completed' },
];

/* -------------------------------------------------------------------- */
/*  Column factories                                                    */
/* -------------------------------------------------------------------- */
const recoveriesColumns = (navigate) => [
  { id: 'case', header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => (
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{r.country || '—'}</span>
        <span className="font-medium text-slate-800 truncate">{r.patient_name || '—'}</span>
      </div>
    ) },
  { id: 'treatment', header: 'Treatment',
    render: (r) => <span className="text-slate-700 truncate">{r.treatment_name || r.treatment_slug || '—'}</span> },
  { id: 'day', header: 'Recovery day', align: 'right',
    render: (r) => r.recovery_day != null
      ? <Pill tone={r.recovery_day > 60 ? 'slate' : r.recovery_day > 14 ? 'amber' : 'sky'}>D+{r.recovery_day}</Pill>
      : <span className="text-slate-300">—</span> },
  { id: 'step', header: 'Current step',
    render: (r) => r.journey_step ? <Pill tone="sky">{r.journey_step}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'follow_up_status', header: 'Follow-up',
    render: (r) => <Pill tone={FOLLOWUP_TONE[r.follow_up_status] || 'slate'}>{(r.follow_up_status || '—').replace(/_/g, ' ')}</Pill> },
  { id: 'last_contact', header: 'Last contact',
    render: (r) => r.last_contact_at ? <span className="text-slate-500">{fmtRel(r.last_contact_at)}</span> : <span className="text-slate-300">—</span> },
  { id: 'last_submission', header: 'Last submission',
    render: (r) => r.last_submission_at ? <span className="text-slate-500">{fmtRel(r.last_submission_at)}</span> : <span className="text-slate-300">—</span> },
  { id: 'rep', header: 'Assigned',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.assigned_after_sales_id || '—').slice(0, 14)}</span> },
  { id: 'escalation', header: 'Escalation',
    render: (r) => r.escalated ? <Pill tone="rose">Open</Pill> : <span className="text-slate-300">—</span> },
  { id: 'last_activity', header: 'Last activity',
    render: (r) => <span className="text-slate-500">{fmtRel(r.last_activity)}</span> },
  { id: 'open', header: '',
    render: (r) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(`/clinic/cases/${r.case_id}/workflow`); }}
        className="text-sky-700 hover:underline text-[11px] inline-flex items-center gap-1"
        data-testid={`as-open-case-${r.case_id}`}
      >
        Open <ExternalLink className="h-3 w-3" />
      </button>
    ),
    width: 'w-16',
  },
];

const followupsColumns = () => [
  { id: 'case', header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase text-slate-500">{r.country || '—'}</span>
        <span className="text-slate-800 truncate">{r.patient_name || '—'}</span>
      </div>
    ) },
  { id: 'title', header: 'Item', render: (r) => <span className="text-slate-800 truncate">{r.title}</span> },
  { id: 'owner', header: 'Owner', render: (r) => <span className="text-slate-600">{r.owner || '—'}</span> },
  { id: 'due', header: 'Due', render: (r) => r.due_at ? fmtDate(r.due_at) : '—' },
  { id: 'follow_up_status', header: 'Follow-up',
    render: (r) => <Pill tone={FOLLOWUP_TONE[r.follow_up_status] || 'slate'}>{(r.follow_up_status || '—').replace(/_/g, ' ')}</Pill> },
  { id: 'sat', header: 'Status',
    render: (r) => r.satisfaction_status === 'satisfied'
      ? <Pill tone="emerald">Satisfied</Pill> : <Pill tone="amber">Pending</Pill> },
  { id: 'via', header: 'Via',
    render: (r) => r.satisfied_via ? <Pill tone="slate">{r.satisfied_via}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'when', header: 'Satisfied',
    render: (r) => r.satisfied_at ? <span className="text-slate-500">{fmtRel(r.satisfied_at)}</span> : <span className="text-slate-300">—</span> },
];

const evidenceColumns = () => [
  { id: 'case', header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => <span className="text-slate-800 truncate">{r.patient_name || '—'}</span> },
  { id: 'req', header: 'Requirement',
    render: (r) => <span className="text-slate-800 truncate">{r.requirement_title || '—'}</span> },
  { id: 'type', header: 'Type',
    render: (r) => <Pill tone={
      r.type === 'DOCUMENT' ? 'sky' : r.type === 'CONSENT' ? 'violet' : r.type === 'PATIENT_ACTION' ? 'amber' : 'slate'
    }>{r.type}</Pill> },
  { id: 'doc', header: 'Doc / Key',
    render: (r) => r.legal_document_id
      ? <span className="font-mono text-[10.5px] text-slate-600">{r.legal_document_id.slice(0,12)} v{r.legal_document_version || '—'}</span>
      : r.consent_content_key
        ? <span className="font-mono text-[10.5px] text-slate-600">{r.consent_content_key} v{r.consent_content_version || '—'}</span>
        : <span className="text-slate-300">—</span> },
  { id: 'file', header: 'File',
    render: (r) => <span className="text-slate-700 truncate">{r.file_name || (r.consent_id ? `consent:${r.consent_id.slice(0,12)}` : '—')}</span> },
  { id: 'submitted', header: 'Submitted',
    render: (r) => r.submitted_at ? <span className="text-slate-500">{fmtRel(r.submitted_at)}</span> : <span className="text-slate-300">—</span> },
  { id: 'review', header: 'Review',
    render: (r) => <Pill tone={r.review_status === 'satisfied' ? 'emerald' : 'amber'}>{r.review_status}</Pill> },
  { id: 'doctor', header: 'Doctor',
    render: (r) => <Pill tone={r.doctor_status === 'reviewed' ? 'emerald' : r.doctor_status === 'submitted' ? 'sky' : 'amber'}>{r.doctor_status}</Pill> },
];

const escalationsColumns = () => [
  { id: 'case', header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => <span className="text-slate-800 truncate">{r.patient_name || '—'}</span> },
  { id: 'treatment', header: 'Treatment',
    render: (r) => <span className="text-slate-700 truncate">{r.treatment_name || '—'}</span> },
  { id: 'reason', header: 'Reason',
    render: (r) => <Pill tone="rose">{r.reason}</Pill> },
  { id: 'by', header: 'Escalated by',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.escalated_by || '—').slice(0,16)}</span> },
  { id: 'when', header: 'Escalated at',
    render: (r) => r.escalated_at ? <span className="text-slate-500">{fmtRel(r.escalated_at)}</span> : <span className="text-slate-300">—</span> },
  { id: 'owner', header: 'Current owner',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.current_owner || '—').slice(0,16)}</span> },
  { id: 'status', header: 'Status',
    render: (r) => <Pill tone={r.status === 'open' ? 'rose' : 'emerald'}>{r.status}</Pill> },
];

const historyColumns = () => [
  { id: 'case', header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'action', header: 'Action',
    render: (r) => <Pill tone={
      r.action?.startsWith('after_sales:followed_up') ? 'emerald'
      : r.action?.startsWith('after_sales:request_update') ? 'amber'
      : r.action?.startsWith('after_sales:escalate') ? 'rose'
      : r.action?.startsWith('doctor_review:reviewed') ? 'emerald'
      : r.action?.startsWith('doctor_review:request_update') ? 'amber'
      : r.action?.startsWith('doctor_review:escalate') ? 'rose'
      : r.action?.startsWith('override-undo') ? 'slate'
      : r.action?.startsWith('override') ? 'violet'
      : r.action === 'rematerialize' ? 'amber'
      : 'sky'
    }>{r.action || '—'}</Pill> },
  { id: 'actor', header: 'Actor',
    render: (r) => <span className="text-slate-600 font-mono text-[10.5px]">{(r.actor || '').slice(0,18)}</span> },
  { id: 'detail', header: 'Detail',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-500 truncate" title={JSON.stringify(r.after || {})}>{JSON.stringify(r.after || {}).slice(0,80)}</span> },
  { id: 'when', header: 'When',
    render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
];

const completionColumns = () => [
  { id: 'case', header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => <span className="text-slate-800 truncate">{r.patient_name || '—'}</span> },
  { id: 'treatment', header: 'Treatment',
    render: (r) => <span className="text-slate-700 truncate">{r.treatment_name || r.treatment_slug || '—'}</span> },
  { id: 'day', header: 'Recovery day', align: 'right',
    render: (r) => r.recovery_day != null ? <Pill tone="sky">D+{r.recovery_day}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'pct', header: 'Satisfied %', align: 'right',
    render: (r) => <span className="font-semibold text-slate-800">{Math.round((r.satisfied_pct || 0) * 100)}%</span> },
  { id: 'bucket', header: 'Stage',
    render: (r) => <Pill tone={
      r.completion_bucket === 'closed' ? 'emerald'
      : r.completion_bucket === 'approaching_closure' ? 'sky'
      : r.completion_bucket === 'requiring_reopen' ? 'rose'
      : 'amber'
    }>{(r.completion_bucket || '—').replace(/_/g, ' ')}</Pill> },
  { id: 'rep', header: 'Assigned',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.assigned_after_sales_id || '—').slice(0, 14)}</span> },
  { id: 'last_activity', header: 'Last activity',
    render: (r) => <span className="text-slate-500">{fmtRel(r.last_activity)}</span> },
];

const ROW_KEY_FOR = {
  recoveries:  (r) => r.case_id,
  followups:   (r) => `${r.case_id}::${r.row_id}`,
  evidence:    (r) => `${r.case_id}::${r.row_id}`,
  escalations: (r) => r.case_id,
  history:     (r) => r.revision_id,
  completion:  (r) => r.case_id,
};

/* -------------------------------------------------------------------- */
/*  Main component                                                      */
/* -------------------------------------------------------------------- */
export default function AfterSalesWorkspace() {
  const navigate = useNavigate();
  const [view, setView] = useState('recoveries');
  const [bucket, setBucket] = useState('all');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [acting, setActing] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/after-sales/workspace/summary');
      if (data?.success) setSummary(data);
    } catch { setSummary(null); }
  }, []);

  const fetchView = useCallback(async () => {
    setLoading(true);
    setActiveRow(null);
    setItems([]);
    try {
      const path = VIEWS.find((v) => v.id === view)?.path;
      const query = view === 'recoveries' && bucket !== 'all' ? `?bucket=${bucket}` : '';
      const { data } = await api.get(`/after-sales/workspace/${path}${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [view, bucket]);

  useEffect(() => {
    fetchSummary();
    fetchView();
  }, [fetchSummary, fetchView, reloadTick]);

  const columns = useMemo(() => {
    if (view === 'recoveries')  return recoveriesColumns(navigate);
    if (view === 'followups')   return followupsColumns();
    if (view === 'evidence')    return evidenceColumns();
    if (view === 'escalations') return escalationsColumns();
    if (view === 'completion')  return completionColumns();
    return historyColumns();
  }, [view, navigate]);

  const chips = useMemo(() => {
    if (view !== 'recoveries') return [];
    return BUCKETS.map((c) => {
      let count;
      if (c.id === 'all')              count = summary?.buckets?.all;
      else if (c.id === 'mine')        count = summary?.buckets?.mine;
      else if (c.id === 'needs_follow_up') count = summary?.buckets?.needs_follow_up;
      else if (c.id === 'overdue')     count = summary?.buckets?.overdue_follow_up;
      else if (c.id === 'waiting_patient') count = summary?.buckets?.waiting_patient;
      else if (c.id === 'escalated')   count = summary?.buckets?.escalated;
      else if (c.id === 'completed')   count = summary?.buckets?.completed_recoveries;
      return { ...c, count: count ?? 0 };
    });
  }, [summary, view]);

  const rowKey = ROW_KEY_FOR[view];

  /* ---------------------- action handler ---------------------------- */
  const submitAction = useCallback(async (action) => {
    if (!activeRow || !activeRow.case_id || !activeRow.row_id) return;
    setActing(true);
    try {
      const { data } = await api.post(
        `/after-sales/workspace/cases/${activeRow.case_id}/rows/${activeRow.row_id}/action`,
        { action },
      );
      if (data?.success) {
        toast.success(`Action saved: ${action}`);
        setActiveRow(null);
        setReloadTick((t) => t + 1);
      } else {
        toast.error(data?.message || 'Action failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }, [activeRow]);

  return (
    <div data-testid="after-sales-workspace" className="min-h-[calc(100vh-4rem)] bg-slate-50 flex flex-col">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20" data-testid="as-header">
        <div className="px-5 py-2 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <LifeBuoy className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">After-Sales Workspace</div>
              <div className="text-[13px] font-semibold text-slate-800 truncate" data-testid="as-clinic-name">
                Clinic · {summary?.clinic_id || '—'} {summary?.is_after_sales === false && (
                  <span className="ml-2 text-[10px] uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">read-only</span>
                )}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
            <span data-testid="as-rep-id">{summary?.rep_id || '—'}</span>
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 text-slate-700"
              data-testid="as-refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        <nav className="px-5 border-t border-slate-100 flex items-center gap-0 overflow-x-auto" data-testid="as-view-tabs">
          {VIEWS.map((v) => {
            const active = v.id === view;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                data-testid={`as-tab-${v.id}`}
                className={`text-[12px] px-3 py-2 border-b-2 transition-colors -mb-px ${
                  active ? 'border-slate-900 text-slate-900 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {v.label}
                {v.id === 'recoveries' && summary?.buckets && (
                  <span className="ml-1.5 text-[10px] text-slate-400">{summary.buckets.all}</span>
                )}
                {v.id === 'escalations' && summary?.buckets?.escalated > 0 && (
                  <span className="ml-1.5 text-[10px] text-rose-500">{summary.buckets.escalated}</span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      {/* FILTER CHIPS or summary strip */}
      {view === 'recoveries' ? (
        <FilterChips
          chips={chips}
          activeId={bucket}
          onChange={setBucket}
          testIdPrefix="as-filter-chip"
          resultLabel={`${items.length} of ${summary?.buckets?.all ?? 0} case(s)`}
        />
      ) : (
        <div className="px-5 py-2 border-b border-slate-100 bg-white flex items-center gap-2 text-[11px] text-slate-500">
          <span>{items.length} row(s)</span>
          {summary?.buckets && (
            <>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.all} cases</span>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.escalated} escalated</span>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.overdue_follow_up} overdue</span>
            </>
          )}
        </div>
      )}

      {/* GRID + DRAWER */}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="as"
            columns={columns}
            rows={items}
            rowKey={rowKey}
            activeRowId={activeRow ? rowKey(activeRow) : null}
            onRowClick={setActiveRow}
            loading={loading}
            loadingMessage="Loading workspace…"
            emptyMessage={
              view === 'recoveries'  ? 'No recoveries in this bucket.'
              : view === 'followups' ? 'No follow-ups.'
              : view === 'evidence'  ? 'No evidence rows.'
              : view === 'escalations' ? 'No escalations.'
              : view === 'completion' ? 'No completion candidates.'
              : 'No revisions yet.'
            }
            selectable={false}
            maxHeightClass="h-[calc(100vh-220px)]"
          />
        </div>

        {activeRow && (
          <AsDrawer
            view={view}
            row={activeRow}
            onClose={() => setActiveRow(null)}
            onJumpToCase={(cid) => navigate(`/clinic/cases/${cid}/workflow`)}
            onAction={submitAction}
            acting={acting}
            isRep={!!summary?.is_after_sales}
          />
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Drawer per view                                                     */
/* -------------------------------------------------------------------- */
function AsDrawer({ view, row, onClose, onJumpToCase, onAction, acting, isRep }) {
  const title =
    view === 'recoveries'  ? `${row.patient_name} · ${row.treatment_name || row.treatment_slug || ''}`
    : view === 'followups' ? row.title
    : view === 'evidence'  ? (row.requirement_title || row.file_name || row.row_id)
    : view === 'escalations' ? `${row.patient_name} · ${row.reason}`
    : view === 'completion' ? `${row.patient_name} · ${(row.completion_bucket || '').replace(/_/g,' ')}`
    : (row.action || 'Revision');
  const eyebrow =
    view === 'recoveries'  ? `Recovery · #${(row.case_id || '').slice(-6)}`
    : view === 'followups' ? `Follow-Up · #${(row.case_id || '').slice(-6)}`
    : view === 'evidence'  ? `Evidence · #${(row.case_id || '').slice(-6)}`
    : view === 'escalations' ? `Escalation · #${(row.case_id || '').slice(-6)}`
    : view === 'completion' ? `Completion · #${(row.case_id || '').slice(-6)}`
    : `Revision · #${(row.case_id || '').slice(-6)}`;

  const showActions = isRep
    && (view === 'followups' || view === 'evidence')
    && row.satisfaction_status !== 'satisfied'
    && (row.review_status !== 'satisfied');

  const footer = (
    <div className="space-y-2">
      {showActions && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="as-drawer-actions">
          <button
            type="button"
            disabled={acting}
            onClick={() => onAction('followed_up')}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="as-action-followed-up"
          >
            <CheckCircle2 className="h-3 w-3" /> Mark followed up
          </button>
          <button
            type="button"
            disabled={acting}
            onClick={() => onAction('request_update')}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            data-testid="as-action-request-update"
          >
            <RotateCcw className="h-3 w-3" /> Request update
          </button>
          <button
            type="button"
            disabled={acting}
            onClick={() => onAction('escalate')}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            data-testid="as-action-escalate"
          >
            <AlertTriangle className="h-3 w-3" /> Escalate
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] text-slate-500 font-mono truncate">{row.row_id || row.case_id || ''}</span>
        {row.case_id && (
          <button
            type="button"
            onClick={() => onJumpToCase(row.case_id)}
            className="text-[11px] px-2 py-1 bg-slate-900 text-white rounded hover:bg-slate-800 inline-flex items-center gap-1"
            data-testid="as-drawer-open-case"
          >
            Open case <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <DetailDrawer open onClose={onClose} title={title} eyebrow={eyebrow} footer={footer} testId="as-drawer">
      {view === 'recoveries' && (
        <>
          <DrawerField label="Patient"         value={row.patient_name || '—'} />
          <DrawerField label="Country"         value={row.country || '—'} />
          <DrawerField label="Treatment"       value={row.treatment_name || row.treatment_slug || '—'} />
          <DrawerField label="Recovery day"    value={row.recovery_day != null ? `D+${row.recovery_day}` : '—'} />
          <DrawerField label="Current step"    value={row.journey_step || '—'} />
          <DrawerField label="Follow-up"       value={(row.follow_up_status || '—').replace(/_/g, ' ')} />
          <DrawerField label="After-sales rows" value={row.assigned} />
          <DrawerField label="Pending Items"     value={row.outstanding} />
          <DrawerField label="Overdue"         value={row.overdue} />
          <DrawerField label="Waiting patient" value={row.waiting_patient} />
          <DrawerField label="Last contact"    value={row.last_contact_at ? fmtRel(row.last_contact_at) : '—'} />
          <DrawerField label="Last submission" value={row.last_submission_at ? fmtRel(row.last_submission_at) : '—'} />
          <DrawerField label="Escalation"      value={row.escalated ? 'Open' : '—'} />
          <DrawerField label="Assigned rep"    value={row.assigned_after_sales_id || '—'} />
          <DrawerField label="Coordinator"     value={row.assigned_coordinator_id || '—'} />
          <DrawerField label="Doctor"          value={row.assigned_doctor_id || '—'} />
          <DrawerField label="Last activity"   value={fmtRel(row.last_activity)} />
        </>
      )}
      {view === 'followups' && (
        <>
          <DrawerField label="Case"    value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient" value={row.patient_name || '—'} />
          <DrawerField label="Owner"   value={row.owner || '—'} />
          <DrawerField label="Due"     value={fmtDate(row.due_at)} />
          <DrawerField label="Follow-up status" value={row.follow_up_status} />
          <DrawerField label="Status"  value={row.satisfaction_status || '—'} />
          {row.satisfied_at && <DrawerField label="Satisfied" value={fmtRel(row.satisfied_at)} />}
          {row.satisfied_via && <DrawerField label="Via"       value={row.satisfied_via} />}
        </>
      )}
      {view === 'evidence' && (
        <>
          <DrawerField label="Case"        value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient"     value={row.patient_name || '—'} />
          <DrawerField label="Requirement" value={row.requirement_title || '—'} />
          <DrawerField label="Type"        value={row.type} />
          {row.legal_document_id   && <DrawerField label="Legal doc" value={`${row.legal_document_id} v${row.legal_document_version || '—'}`} />}
          {row.consent_content_key && <DrawerField label="Consent" value={`${row.consent_content_key} v${row.consent_content_version || '—'}`} />}
          {row.file_name           && <DrawerField label="File" value={row.file_name} />}
          {row.consent_id          && <DrawerField label="Consent ID" value={row.consent_id} />}
          <DrawerField label="Submitted"   value={fmtRel(row.submitted_at)} />
          <DrawerField label="Submitted by" value={row.submitted_by || '—'} />
          <DrawerField label="Review"      value={row.review_status || '—'} />
          <DrawerField label="Doctor"      value={row.doctor_status || '—'} />
          {row.requirement_id && <DrawerField label="Requirement ID" value={row.requirement_id} />}
        </>
      )}
      {view === 'escalations' && (
        <>
          <DrawerField label="Case"          value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient"       value={row.patient_name || '—'} />
          <DrawerField label="Treatment"     value={row.treatment_name || '—'} />
          <DrawerField label="Reason"        value={row.reason} />
          <DrawerField label="Escalated by"  value={row.escalated_by || '—'} />
          <DrawerField label="Escalated at"  value={fmtRel(row.escalated_at)} />
          <DrawerField label="Current owner" value={row.current_owner || '—'} />
          <DrawerField label="Status"        value={row.status} />
        </>
      )}
      {view === 'completion' && (
        <>
          <DrawerField label="Case"          value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient"       value={row.patient_name || '—'} />
          <DrawerField label="Treatment"     value={row.treatment_name || row.treatment_slug || '—'} />
          <DrawerField label="Recovery day"  value={row.recovery_day != null ? `D+${row.recovery_day}` : '—'} />
          <DrawerField label="Satisfied %"   value={`${Math.round((row.satisfied_pct || 0) * 100)}%`} />
          <DrawerField label="Stage"         value={(row.completion_bucket || '—').replace(/_/g, ' ')} />
          <DrawerField label="Assigned rep"  value={row.assigned_after_sales_id || '—'} />
          <DrawerField label="Last activity" value={fmtRel(row.last_activity)} />
        </>
      )}
      {view === 'history' && (
        <>
          <DrawerField label="Case"   value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Action" value={row.action || '—'} />
          <DrawerField label="Actor"  value={row.actor || '—'} />
          <DrawerField label="When"   value={fmtRel(row.created_at)} />
          {row.before && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Before</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">{JSON.stringify(row.before, null, 2)}</pre>
            </div>
          )}
          {row.after && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">After</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">{JSON.stringify(row.after, null, 2)}</pre>
            </div>
          )}
        </>
      )}
    </DetailDrawer>
  );
}
